import type {
  ImportJobSummary,
  SourcePlatform,
} from '~entities/import-job/api/types';

import { PLATFORM_OPTIONS } from '~features/import-wizard/constants';

import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent } from '~shared/shadcn/ui/card';

interface Step0WelcomeProps {
  value: SourcePlatform | null;
  openDrafts: ImportJobSummary[];
  onResumeDraft: (draft: ImportJobSummary) => void;
  onDiscardDraft?: (draft: ImportJobSummary) => void;
  /** Close or stop a non-draft open session so a new import can start. */
  onCloseSession?: (draft: ImportJobSummary) => void;
  closingJobId?: string | null;
  onChange: (platform: SourcePlatform) => void;
  onNext: () => void;
}

function draftLabel(draft: ImportJobSummary): string {
  if (draft.status === 'completed') return 'Import complete — review results';
  if (draft.status === 'failed') return 'Import failed — review errors';
  if (draft.status === 'processing' || draft.status === 'queued') {
    return 'Import in progress';
  }
  const parts: string[] = [];
  if (draft.hasClassCsv) parts.push('classes');
  if (draft.hasParentCsv) parts.push('families');
  if (draft.stagedImageCount > 0) {
    parts.push(`${draft.stagedImageCount} photo(s)`);
  }
  const summary = parts.length > 0 ? parts.join(', ') : 'in progress';
  return `Step ${draft.wizardStepReached} · ${summary}`;
}

export function Step0Welcome({
  value,
  openDrafts,
  onResumeDraft,
  onDiscardDraft,
  onCloseSession,
  closingJobId = null,
  onChange,
  onNext,
}: Step0WelcomeProps) {
  const resumableDrafts = openDrafts.filter(
    (draft) =>
      draft.status === 'draft' ||
      draft.status === 'queued' ||
      draft.status === 'processing' ||
      draft.status === 'completed' ||
      draft.status === 'failed',
  );

  return (
    <Card className='mx-auto max-w-3xl'>
      <CardContent className='space-y-6 p-6'>
        {resumableDrafts.length > 0 && (
          <div className='rounded-lg border border-primary/30 bg-primary/5 p-4'>
            <h3 className='font-medium'>Continue a saved import</h3>
            <p className='mt-1 text-sm text-muted-foreground'>
              Pick up where you left off. Your uploaded files and settings are
              saved on the server.
            </p>
            <ul className='mt-4 space-y-2'>
              {resumableDrafts.map((draft) => (
                <li
                  key={draft.jobId}
                  className='flex flex-wrap items-center justify-between gap-2 rounded-md border bg-background p-3 text-sm'
                >
                  <div>
                    <div className='font-medium capitalize'>
                      {draft.sourcePlatform.replace(/_/g, ' ')}
                    </div>
                    <div className='text-muted-foreground'>
                      {draftLabel(draft)}
                    </div>
                  </div>
                  <div className='flex flex-wrap gap-2'>
                    {draft.status === 'draft' && onDiscardDraft && (
                      <Button
                        size='sm'
                        variant='ghost'
                        className='text-destructive hover:text-destructive'
                        onClick={() => onDiscardDraft(draft)}
                      >
                        Discard
                      </Button>
                    )}
                    {draft.status !== 'draft' && onCloseSession && (
                      <Button
                        size='sm'
                        variant='ghost'
                        className='text-destructive hover:text-destructive'
                        disabled={closingJobId === draft.jobId}
                        onClick={() => onCloseSession(draft)}
                      >
                        {closingJobId === draft.jobId
                          ? 'Closing…'
                          : draft.status === 'queued' ||
                              draft.status === 'processing'
                            ? 'Stop & close'
                            : 'Close'}
                      </Button>
                    )}
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() => onResumeDraft(draft)}
                    >
                      Resume
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <h2 className='text-xl font-semibold'>
            Where are you bringing your studio from?
          </h2>
          <p className='mt-2 text-muted-foreground'>
            This helps us match your spreadsheet columns so you spend less time
            mapping fields.
          </p>
        </div>
        <div className='grid gap-3 sm:grid-cols-2'>
          {PLATFORM_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type='button'
              onClick={() => onChange(opt.id)}
              className={`rounded-lg border p-4 text-left transition-colors ${
                value === opt.id
                  ? 'border-primary bg-primary/5 ring-1 ring-primary'
                  : 'hover:bg-muted/50'
              }`}
            >
              <div className='font-medium'>{opt.label}</div>
            </button>
          ))}
          <button
            type='button'
            disabled
            className='cursor-not-allowed rounded-lg border border-dashed p-4 text-left text-muted-foreground opacity-70'
          >
            <div className='font-medium'>
              Other Platform? Contact support for assistance.
            </div>
          </button>
        </div>
        <div className='flex justify-end'>
          <Button disabled={value == null} onClick={onNext}>
            Continue
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
